import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../components/AdminLayout';
import { useNavigate, useParams } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const UNIT_TYPES = [
  'Per Person',
  'Per Room',
  'Per Night',
  'Per Room/Night',
  'Fixed Total',
  'Per Vehicle',
  'Per Day',
  'Per Unit'
];

const CURRENCIES = ['USD', 'EUR', 'GBP', 'IDR', 'SGD', 'AUD'];
const CATEGORIES = ['Accommodation', 'Transportation', 'Activities', 'Guide', 'Meals', 'Entrance Fees', 'Other'];

export function AdminCostingFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    client_name: '',
    contact_person: '',
    travel_start_date: '',
    travel_end_date: '',
    destination: '',
    pax: 1,
    rooms: 1,
    quotation_currency: 'IDR',
    quotation_exchange_rate: 1.0,
    notes: '',
    status: 'Draft',
    pricing_method: 'Markup',
    pricing_percentage: 20
  });

  const [costItems, setCostItems] = useState([]);
  const [calculations, setCalculations] = useState({
    total_idr_cost: 0,
    idr_selling_price: 0,
    operating_profit_idr: 0,
    profit_margin_percent: 0,
    quotation_amount: 0,
    quotation_currency: 'IDR',
    quotation_exchange_rate: 1.0
  });

  useEffect(() => {
    if (isEdit) {
      fetchCosting();
    }
  }, [id]);

  useEffect(() => {
    if (isEdit) {
      fetchCalculations();
    }
  }, [costItems, formData.pricing_method, formData.pricing_percentage]);

  const fetchCosting = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/costings/${id}`, {
        credentials: 'include'
      });
      const data = await response.json();
      setFormData({
        client_name: data.client_name,
        contact_person: data.contact_person || '',
        travel_start_date: data.travel_start_date,
        travel_end_date: data.travel_end_date,
        destination: data.destination,
        pax: data.pax,
        rooms: data.rooms,
        base_currency: data.quotation_currency || data.base_currency || 'IDR',
        quotation_currency: data.quotation_currency || 'IDR',
        quotation_exchange_rate: data.quotation_exchange_rate || 1.0,
        notes: data.notes || '',
        status: data.status,
        pricing_method: data.pricing_method,
        pricing_percentage: data.pricing_percentage
      });
      setCostItems(data.cost_items || []);
      
      // Set initial calculations from costing data
      if (data.total_idr_cost !== undefined) {
        setCalculations({
          total_idr_cost: data.total_idr_cost || 0,
          idr_selling_price: data.idr_selling_price || 0,
          operating_profit_idr: data.operating_profit_idr || 0,
          profit_margin_percent: data.profit_margin_percent || 0,
          quotation_amount: data.quotation_amount || 0,
          quotation_currency: data.quotation_currency || 'IDR',
          quotation_exchange_rate: data.quotation_exchange_rate || 1.0
        });
      }
    } catch (error) {
      console.error('Failed to fetch costing:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCalculations = async () => {
    if (!id) return;
    try {
      const response = await fetch(`${API_URL}/api/admin/costings/${id}/calculations`, {
        credentials: 'include'
      });
      const data = await response.json();
      setCalculations(data);
    } catch (error) {
      console.error('Failed to fetch calculations:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const url = isEdit
        ? `${API_URL}/api/admin/costings/${id}`
        : `${API_URL}/api/admin/costings`;
      
      const method = isEdit ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...formData,
          cost_items: costItems
        })
      });

      if (!response.ok) throw new Error('Failed to save costing');

      const data = await response.json();
      navigate(`/admin/costing/${data.id}`);
    } catch (error) {
      alert('Failed to save costing: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const addCostItem = () => {
    setCostItems([...costItems, {
      id: Date.now().toString(),
      category: 'Accommodation',
      description: '',
      supplier: '',
      cost_currency: 'IDR',
      cost_amount: 0,
      quantity: 1,
      unit_type: 'Per Person',
      nights: 1,
      exchange_rate_to_idr: 1.0,
      notes: ''
    }]);
  };

  const updateCostItem = (index, field, value) => {
    const updated = [...costItems];
    updated[index][field] = value;
    setCostItems(updated);
  };

  const deleteCostItem = (index) => {
    setCostItems(costItems.filter((_, i) => i !== index));
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-8 text-white">Loading...</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-serif text-white mb-2">
            {isEdit ? 'Edit Costing' : 'New Costing'}
          </h1>
          <p className="text-gray-400">Create and manage travel costing</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* General Details */}
          <div className="bg-[#0F1629] border border-[#1E293B] rounded-lg p-6">
            <h2 className="text-xl font-serif text-white mb-6">General Details</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Client / Company Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.client_name}
                  onChange={(e) => setFormData({...formData, client_name: e.target.value})}
                  className="w-full px-4 py-2 bg-[#0A0F1E] border border-[#1E293B] rounded-lg text-white focus:border-[#C4A962] focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Contact Person
                </label>
                <input
                  type="text"
                  value={formData.contact_person}
                  onChange={(e) => setFormData({...formData, contact_person: e.target.value})}
                  className="w-full px-4 py-2 bg-[#0A0F1E] border border-[#1E293B] rounded-lg text-white focus:border-[#C4A962] focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Travel Start Date *
                </label>
                <input
                  type="date"
                  required
                  value={formData.travel_start_date}
                  onChange={(e) => setFormData({...formData, travel_start_date: e.target.value})}
                  className="w-full px-4 py-2 bg-[#0A0F1E] border border-[#1E293B] rounded-lg text-white focus:border-[#C4A962] focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Travel End Date *
                </label>
                <input
                  type="date"
                  required
                  value={formData.travel_end_date}
                  onChange={(e) => setFormData({...formData, travel_end_date: e.target.value})}
                  className="w-full px-4 py-2 bg-[#0A0F1E] border border-[#1E293B] rounded-lg text-white focus:border-[#C4A962] focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Destination *
                </label>
                <input
                  type="text"
                  required
                  value={formData.destination}
                  onChange={(e) => setFormData({...formData, destination: e.target.value})}
                  className="w-full px-4 py-2 bg-[#0A0F1E] border border-[#1E293B] rounded-lg text-white focus:border-[#C4A962] focus:outline-none"
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Pax *
                  </label>
                  <input
                    type="number"
                    required
                    min="1"
                    value={formData.pax}
                    onChange={(e) => setFormData({...formData, pax: parseInt(e.target.value)})}
                    className="w-full px-4 py-2 bg-[#0A0F1E] border border-[#1E293B] rounded-lg text-white focus:border-[#C4A962] focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Rooms *
                  </label>
                  <input
                    type="number"
                    required
                    min="1"
                    value={formData.rooms}
                    onChange={(e) => setFormData({...formData, rooms: parseInt(e.target.value)})}
                    className="w-full px-4 py-2 bg-[#0A0F1E] border border-[#1E293B] rounded-lg text-white focus:border-[#C4A962] focus:outline-none"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Internal Currency
                  </label>
                  <div className="w-full px-4 py-2 bg-[#1E293B] border border-[#1E293B] rounded-lg text-gray-400">
                    IDR (Fixed)
                  </div>
                  <div className="text-xs text-gray-500 mt-1">Internal costing currency is always IDR</div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Quotation Currency *
                  </label>
                  <select
                    value={formData.quotation_currency}
                    onChange={(e) => setFormData({...formData, quotation_currency: e.target.value})}
                    className="w-full px-4 py-2 bg-[#0A0F1E] border border-[#1E293B] rounded-lg text-white focus:border-[#C4A962] focus:outline-none"
                  >
                    {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <div className="text-xs text-gray-500 mt-1">Currency for client quotation</div>
                </div>
              </div>

              {formData.quotation_currency !== 'IDR' && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Quotation Exchange Rate *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.quotation_exchange_rate}
                    onChange={(e) => setFormData({...formData, quotation_exchange_rate: parseFloat(e.target.value)})}
                    className="w-full px-4 py-2 bg-[#0A0F1E] border border-[#1E293B] rounded-lg text-white focus:border-[#C4A962] focus:outline-none"
                  />
                  <div className="text-xs text-gray-500 mt-1">
                    1 {formData.quotation_currency} = IDR {formData.quotation_exchange_rate.toLocaleString()}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Cost Items */}
          <div className="bg-[#0F1629] border border-[#1E293B] rounded-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-serif text-white">Cost Items</h2>
              <button
                type="button"
                onClick={addCostItem}
                className="px-4 py-2 bg-[#C4A962] hover:bg-[#B89952] text-[#0A0F1E] rounded-lg text-sm transition-colors"
              >
                + Add Item
              </button>
            </div>

            <div className="space-y-4">
              {costItems.map((item, index) => (
                <CostItemRow
                  key={item.id}
                  item={item}
                  index={index}
                  baseCurrency={formData.base_currency}
                  onUpdate={updateCostItem}
                  onDelete={deleteCostItem}
                />
              ))}

              {costItems.length === 0 && (
                <div className="text-center py-8 text-gray-400">
                  No cost items yet. Click "Add Item" to start.
                </div>
              )}
            </div>
          </div>

          {/* Pricing */}
          <div className="bg-[#0F1629] border border-[#1E293B] rounded-lg p-6">
            <h2 className="text-xl font-serif text-white mb-6">Pricing</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Pricing Method *
                </label>
                <select
                  value={formData.pricing_method}
                  onChange={(e) => setFormData({...formData, pricing_method: e.target.value})}
                  className="w-full px-4 py-2 bg-[#0A0F1E] border border-[#1E293B] rounded-lg text-white focus:border-[#C4A962] focus:outline-none"
                >
                  <option value="Markup">Markup %</option>
                  <option value="Margin">Margin %</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {formData.pricing_method} %
                </label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  value={formData.pricing_percentage}
                  onChange={(e) => setFormData({...formData, pricing_percentage: parseFloat(e.target.value)})}
                  className="w-full px-4 py-2 bg-[#0A0F1E] border border-[#1E293B] rounded-lg text-white focus:border-[#C4A962] focus:outline-none"
                />
              </div>
            </div>

            <div className="mt-6 pt-6 border-t border-[#1E293B]">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <div className="text-sm text-gray-400 mb-1">Internal Cost (IDR)</div>
                  <div className="text-xl text-white font-semibold">IDR {(calculations.total_idr_cost || 0).toLocaleString()}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-400 mb-1">IDR Selling Price</div>
                  <div className="text-xl text-[#C4A962] font-semibold">IDR {(calculations.idr_selling_price || 0).toLocaleString()}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-400 mb-1">Operating Profit (IDR)</div>
                  <div className="text-xl text-green-400 font-semibold">IDR {(calculations.operating_profit_idr || 0).toLocaleString()}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-400 mb-1">Profit Margin</div>
                  <div className="text-xl text-white font-semibold">{(calculations.profit_margin_percent || 0).toFixed(2)}%</div>
                </div>
                <div className="md:col-span-2">
                  <div className="text-sm text-gray-400 mb-1">Client Quotation</div>
                  <div className="text-xl text-white font-semibold">
                    {calculations.quotation_currency || formData.quotation_currency || 'IDR'} {(calculations.quotation_amount || 0).toLocaleString()}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    Rate: 1 {calculations.quotation_currency || 'IDR'} = IDR {(calculations.quotation_exchange_rate || 1).toLocaleString()}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={() => navigate('/admin/costing')}
              className="px-6 py-3 bg-[#1E293B] hover:bg-[#2E3A4B] text-white rounded-lg transition-colors"
            >
              Cancel
            </button>

            <div className="flex gap-3">
              <button
                type="submit"
                disabled={saving}
                onClick={() => setFormData({...formData, status: 'Draft'})}
                className="px-6 py-3 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save Draft'}
              </button>
              <button
                type="submit"
                disabled={saving}
                onClick={() => setFormData({...formData, status: 'Completed'})}
                className="px-6 py-3 bg-[#C4A962] hover:bg-[#B89952] text-[#0A0F1E] font-semibold rounded-lg transition-colors disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save & Complete'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
}

function CostItemRow({ item, index, baseCurrency, onUpdate, onDelete }) {
  return (
    <div className="bg-[#0A0F1E] border border-[#1E293B] rounded-lg p-4">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div>
          <label className="block text-xs text-gray-400 mb-1">Category</label>
          <select
            value={item.category}
            onChange={(e) => onUpdate(index, 'category', e.target.value)}
            className="w-full px-3 py-2 bg-[#0F1629] border border-[#1E293B] rounded text-white text-sm focus:border-[#C4A962] focus:outline-none"
          >
            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>

        <div className="md:col-span-3">
          <label className="block text-xs text-gray-400 mb-1">Description</label>
          <input
            type="text"
            value={item.description}
            onChange={(e) => onUpdate(index, 'description', e.target.value)}
            className="w-full px-3 py-2 bg-[#0F1629] border border-[#1E293B] rounded text-white text-sm focus:border-[#C4A962] focus:outline-none"
            placeholder="Item description"
          />
        </div>

        <div>
          <label className="block text-xs text-gray-400 mb-1">Supplier</label>
          <input
            type="text"
            value={item.supplier}
            onChange={(e) => onUpdate(index, 'supplier', e.target.value)}
            className="w-full px-3 py-2 bg-[#0F1629] border border-[#1E293B] rounded text-white text-sm focus:border-[#C4A962] focus:outline-none"
          />
        </div>

        <div>
          <label className="block text-xs text-gray-400 mb-1">Unit Type</label>
          <select
            value={item.unit_type}
            onChange={(e) => onUpdate(index, 'unit_type', e.target.value)}
            className="w-full px-3 py-2 bg-[#0F1629] border border-[#1E293B] rounded text-white text-sm focus:border-[#C4A962] focus:outline-none"
          >
            {UNIT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>

        <div>
          <label className="block text-xs text-gray-400 mb-1">Currency</label>
          <select
            value={item.cost_currency}
            onChange={(e) => onUpdate(index, 'cost_currency', e.target.value)}
            className="w-full px-3 py-2 bg-[#0F1629] border border-[#1E293B] rounded text-white text-sm focus:border-[#C4A962] focus:outline-none"
          >
            {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>

        <div>
          <label className="block text-xs text-gray-400 mb-1">Cost Amount</label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={item.cost_amount || item.unit_cost || 0}
            onChange={(e) => onUpdate(index, 'cost_amount', parseFloat(e.target.value))}
            className="w-full px-3 py-2 bg-[#0F1629] border border-[#1E293B] rounded text-white text-sm focus:border-[#C4A962] focus:outline-none"
          />
        </div>

        <div>
          <label className="block text-xs text-gray-400 mb-1">Quantity</label>
          <input
            type="number"
            min="1"
            value={item.quantity}
            onChange={(e) => onUpdate(index, 'quantity', parseInt(e.target.value))}
            className="w-full px-3 py-2 bg-[#0F1629] border border-[#1E293B] rounded text-white text-sm focus:border-[#C4A962] focus:outline-none"
          />
        </div>

        {(item.unit_type.includes('Night') || item.unit_type === 'Per Day') && (
          <div>
            <label className="block text-xs text-gray-400 mb-1">
              {item.unit_type === 'Per Day' ? 'Days' : 'Nights'}
            </label>
            <input
              type="number"
              min="1"
              value={item.nights}
              onChange={(e) => onUpdate(index, 'nights', parseInt(e.target.value))}
              className="w-full px-3 py-2 bg-[#0F1629] border border-[#1E293B] rounded text-white text-sm focus:border-[#C4A962] focus:outline-none"
            />
          </div>
        )}

        {item.cost_currency !== 'IDR' && (
          <div>
            <label className="block text-xs text-gray-400 mb-1">FX Rate to IDR</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={item.exchange_rate_to_idr || item.exchange_rate || 1.0}
              onChange={(e) => onUpdate(index, 'exchange_rate_to_idr', parseFloat(e.target.value))}
              className="w-full px-3 py-2 bg-[#0F1629] border border-[#1E293B] rounded text-white text-sm focus:border-[#C4A962] focus:outline-none"
            />
            <div className="text-xs text-gray-500 mt-1">
              1 {item.cost_currency} = IDR {(item.exchange_rate_to_idr || 1).toLocaleString()}
            </div>
          </div>
        )}

        <div className="md:col-span-4 flex items-end">
          <button
            type="button"
            onClick={() => onDelete(index)}
            className="text-red-400 hover:text-red-300 text-sm transition-colors"
          >
            🗑️ Delete
          </button>
        </div>
      </div>
    </div>
  );
}
